import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// import loginBanner from '../../assets/images/login/login-banner.png';
// import sample from '../../assets/videos/background.mp4';

import { appCurrentBranding } from '../../../config/branding';
import { branding, PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
import ConfirmBox from '../../../shared-components/modal-box/ConfirmBox';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import { setLocalStorageItem } from '../../../shared-functions/LocalStorage';
import {
  emailValidation,
  phoneNumberValidation,
  textAndNumberValidation,
} from '../../../shared-functions/Validation';
import authApi from '../../../utils/api/auth';

/**
 * Login page function component
 */
function ForgetPassword() {
  const { t, i18n } = useTranslation('loginV2');
  const { portalType } = usePortal();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [credentials, setCredentials] = useState<{
    emailOrPhoneNumber: string;
    type: string | number;
    countryCode: string;
    accountCustomId?: string;
  }>({
    emailOrPhoneNumber: '',
    type: 'email',
    countryCode: appCurrentBranding === branding.VOLTIE ? '1' : '966',
    // PARTNER ONLY
    ...(portalType === PortalType.PARTNER && {
      accountCustomId: '',
    }),
  });
  const [disabled, setDisabled] = useState<boolean>(false);
  const [submitOpen, setSubmitOpen] = useState<boolean>(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState<boolean>(false);
  // Set when the forgot-password API rejects the credentials, so the
  // email-or-phone field can be highlighted even after a failed request.
  const [credentialError, setCredentialError] = useState<boolean>(false);

  /**
   * Navigates back to the login page after the user confirms cancellation.
   */
  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    navigate('../login/credentials');
  };

  /**
   * Input field change function
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    if (name === 'emailOrPhoneNumber') {
      setCredentialError(false);
    }
    if (name === 'type') {
      setCredentials({
        ...credentials,
        [name]: changeValue,
        emailOrPhoneNumber: '',
      });
    } else {
      setCredentials({
        ...credentials,
        [name]: changeValue,
      });
    }
  };

  /**
   * Whether email-or-phone field should show an error state.
   * @returns {boolean} true when field is empty, format invalid, or API rejected credentials
   */
  const hasEmailOrPhoneError = () => {
    if (credentialError) return true;
    if (!submitOpen) return false;
    if (!credentials.emailOrPhoneNumber) return true;
    return credentials.type === 'email'
      ? !emailValidation(credentials.emailOrPhoneNumber)
      : !phoneNumberValidation(credentials.emailOrPhoneNumber);
  };

  /**
   * Resolves which message to show under the email-or-phone field, in
   * priority order: required, format invalid, then API credential error.
   * @returns {string} translated error message
   */
  const emailOrPhoneErrorMessage = () => {
    if (!credentials.emailOrPhoneNumber) {
      return credentials.type === 'email' ? t('pleaseEnterEmail') : t('pleaseEnterPhoneNumber');
    }
    const formatInvalid =
      credentials.type === 'email'
        ? !emailValidation(credentials.emailOrPhoneNumber)
        : !phoneNumberValidation(credentials.emailOrPhoneNumber);
    if (formatInvalid) {
      return credentials.type === 'email' ? t('emailInvalid') : t('phoneInvalid');
    }
    if (portalType === PortalType.PARTNER) {
      return credentials.type === 'email'
        ? t('incorrectEmailOrPassword')
        : t('incorrectPhoneOrPassword');
    }
    return credentials.type === 'email' ? t('incorrectEmail') : t('incorrectPhone');
  };

  /**
   * Whether the partner account-ID field should show an error state.
   * @returns {boolean} true when submitted empty, format invalid, or the API rejected it
   */
  const hasAccountCustomIdError = () => {
    if (!credentials.accountCustomId) return submitOpen;
    return credentialError || !textAndNumberValidation(20, 20, credentials.accountCustomId);
  };

  /**
   * Resolves which message to show under the partner account-ID field, in
   * priority order: required, format invalid, then API credential error.
   * @returns {string} translated error message
   */
  const accountCustomIdErrorMessage = () => {
    if (!credentials.accountCustomId) return t('pleaseEnterAccountId');
    if (!textAndNumberValidation(20, 20, credentials.accountCustomId)) return t('invalidAccountId');
    return t('incorrectAccountId');
  };

  /**
   * handle submit function
   */
  const submitFunction = () => {
    setSubmitOpen(true);
    setCredentialError(false);
    if (
      portalType !== PortalType.PARTNER &&
      credentials.countryCode !== '' &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber))
    ) {
      setDisabled(true);
      const request = {
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
        countryCode: credentials.countryCode,
      };
      authApi
        .forgotPassword(request)
        .then(() => {
          setDisabled(false);
          setLocalStorageItem('passwordReset', credentials);
          navigate('../login/otp-verification');
        })
        .catch((error) => {
          setDisabled(false);

          if (!error.response) {
            dispatch(
              setNotificationMessage({
                message: 'No internet connection. Please check your network and try again.',
                status: true,
                type: 'error',
              }),
            );
            return;
          }

          setCredentialError(true);
          dispatch(
            setNotificationMessage({
              type: 'error',
              status: true,
              message: error?.response?.data?.message || 'Something went wrong!',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
    if (
      portalType === PortalType.PARTNER &&
      credentials.type !== '' &&
      credentials.countryCode !== '' &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber))
    ) {
      setDisabled(true);

      const request: any = {
        countryCode: credentials.countryCode,
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
      };

      if (credentials?.accountCustomId) {
        request.accountCustomId = credentials.accountCustomId;
      }

      authApi
        .forgotPassword(request)
        .then(() => {
          setDisabled(false);

          setLocalStorageItem('passwordReset', credentials);
          navigate('../login/new-password');
        })
        .catch((error) => {
          setDisabled(false);

          if (!error.response) {
            dispatch(
              setNotificationMessage({
                type: 'error',
                status: true,
                message: 'No internet connection. Please check your network and try again.',
              }),
            );
            return;
          }

          setCredentialError(true);
          dispatch(
            setNotificationMessage({
              type: 'error',
              status: true,
              message: error?.response?.data?.message || 'Something went wrong!',
            }),
          );
        });
    }
  };

  return (
    <div className="forgot-password-v2-wrap" dir={i18n.dir()}>
      <h1 className="title">{t('enterEmailorPhonenumber')}</h1>
      <div className="forgot-password-v2-container">
        <div className="email-phone-toggle-wrap">
          {portalType === PortalType.PARTNER && (
            <CustomInput
              inputLabel={t('inputLabelAccountId')}
              type="text"
              placeholder={t('enterAccountId')}
              name="accountCustomId"
              value={credentials.accountCustomId || ''}
              onChangeValue={handleCredentialChange}
              error={hasAccountCustomIdError()}
              errorMessage={accountCustomIdErrorMessage()}
            />
          )}
          <RadioButton
            name="type"
            value={credentials.type}
            list={[
              {
                label: `${t('email')}`,
                value: 'email',
              },
              {
                label: `${t('phoneNumber')}`,
                value: 'phone',
              },
            ]}
            onChangeValue={handleCredentialChange}
          />
          {credentials.type === 'email' ? (
            <CustomInput
              type="email"
              placeholder={t('enterEmail')}
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              onChangeValue={handleCredentialChange}
              error={hasEmailOrPhoneError()}
              errorMessage={emailOrPhoneErrorMessage()}
            />
          ) : (
            <CustomPhoneInput
              type="number"
              placeholder={t('enterPhoneNumber')}
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              value1={credentials.countryCode}
              onChangeValue={handleCredentialChange}
              error={hasEmailOrPhoneError()}
              errorMessage={emailOrPhoneErrorMessage()}
              minLength={5}
              maxLength={13}
            />
          )}
          <div className="forgot-password-button-group">
            <div className="forgot-password-button-item">
              <MyButton
                label={t('backToLogin')}
                buttonType="outline"
                onClickFunc={() => setCancelConfirmOpen(true)}
              />
            </div>
            <div className="forgot-password-button-item">
              <MyButton
                label={t('getOtp')}
                buttonType="submit"
                onClickFunc={submitFunction}
                loading={disabled}
                disableButton={disabled}
              />
            </div>
          </div>
        </div>
      </div>
      <ConfirmBox
        open={cancelConfirmOpen}
        title={t('cancelForgotPasswordTitle')}
        subTitle={t('cancelForgotPasswordSubTitle')}
        conformSubmit={confirmCancel}
        closeModal={() => setCancelConfirmOpen(false)}
        submitLabel={t('yesCancel')}
      />
    </div>
  );
}

export default ForgetPassword;
